/** Build and download a CSV string in the browser. */
export function downloadCsv(filename, rows, headers) {
  const escape = (value) => {
    const text = value == null ? "" : String(value);
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };
  const lines = [
    headers.map(escape).join(","),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function exportTrialBalanceCsv(data) {
  downloadCsv(
    `trial-balance-${data.period_name || "report"}.csv`,
    (data.rows || []).map((r) => ({
      code: r.account_code,
      account: r.account_name,
      type: r.account_type,
      debit: r.debit,
      credit: r.credit,
    })),
    ["code", "account", "type", "debit", "credit"]
  );
}

export function exportBalanceSheetCsv(data) {
  const rows = [];
  const pushSection = (section) => {
    rows.push({ code: "", account: section.title, amount: section.total });
    (section.lines || []).forEach((line) => {
      rows.push({ code: line.account_code, account: line.account_name, amount: line.amount });
    });
  };
  pushSection(data.assets);
  pushSection(data.liabilities);
  pushSection(data.equity);
  rows.push({ code: "", account: "Total assets", amount: data.total_assets });
  rows.push({ code: "", account: "Total liabilities + equity", amount: data.total_liabilities_equity });
  downloadCsv(`balance-sheet-${data.as_of_date || "report"}.csv`, rows, ["code", "account", "amount"]);
}

export function exportIncomeStatementCsv(data) {
  const rows = [];
  const pushSection = (section) => {
    rows.push({ code: "", account: section.title, amount: section.total });
    (section.lines || []).forEach((line) => {
      rows.push({ code: line.account_code, account: line.account_name, amount: line.amount });
    });
  };
  pushSection(data.revenue);
  pushSection(data.expenses);
  rows.push({ code: "", account: "Net income", amount: data.net_income });
  downloadCsv(`income-statement-${data.period_name || "report"}.csv`, rows, ["code", "account", "amount"]);
}

export function exportGeneralLedgerCsv(lines, label = "general-ledger") {
  downloadCsv(
    `${label}.csv`,
    (lines || []).map((r) => ({
      date: r.entry_date,
      entry: r.entry_number,
      account: `${r.account_code} ${r.account_name}`,
      debit: r.debit,
      credit: r.credit,
      description: r.line_description || r.entry_description || "",
    })),
    ["date", "entry", "account", "debit", "credit", "description"]
  );
}

export function exportBudgetVsActualCsv(data) {
  downloadCsv(
    `budget-vs-actual-${data.period_name || "report"}.csv`,
    (data.rows || []).map((r) => ({
      code: r.account_code,
      account: r.account_name,
      budget: r.budget_amount,
      actual: r.actual_amount,
      variance: r.variance,
    })),
    ["code", "account", "budget", "actual", "variance"]
  );
}

export function exportVatSummaryCsv(data) {
  downloadCsv(
    `vat-summary-${data.date_from || ""}-${data.date_to || ""}.csv`,
    (data.rows || []).map((r) => ({
      code: r.code,
      name: r.name,
      type: r.tax_type,
      rate: r.rate_pct,
      base: r.base_amount,
      tax: r.tax_amount,
    })),
    ["code", "name", "type", "rate", "base", "tax"]
  );
}
